import { DEFAULT_LOCALE } from './locales.js';
const isPlural = (value) => typeof value === 'object' && value !== null && 'other' in value;
const lookup = (tree, key) => {
    let node = tree;
    for (const part of key.split('.')) {
        if (typeof node !== 'object' || node === null || isPlural(node)) {
            return undefined;
        }
        node = node[part];
    }
    return typeof node === 'string' || isPlural(node) ? node : undefined;
};
/**
 * Cached because constructing an Intl formatter is not cheap and a list of
 * fifty orders would otherwise build fifty identical ones.
 */
const pluralRules = new Map();
const rulesFor = (locale) => {
    const existing = pluralRules.get(locale);
    if (existing) {
        return existing;
    }
    const created = new Intl.PluralRules(locale);
    pluralRules.set(locale, created);
    return created;
};
/**
 * Substitute `{{name}}` placeholders.
 *
 * Numbers are formatted for the locale on the way in, so "{{count}} items"
 * renders as "1,234 items" in English and "1.234" in German without every call
 * site remembering to format first.
 */
const interpolate = (template, params, locale) => {
    if (!params) {
        return template;
    }
    return template.replace(/\{\{(\w+)\}\}/g, (whole, name) => {
        const value = params[name];
        if (value === undefined) {
            // Leave the placeholder visible rather than printing "undefined": a
            // translator reading the UI can see exactly which variable is missing.
            return whole;
        }
        return typeof value === 'number' ? new Intl.NumberFormat(locale).format(value) : value;
    });
};
/**
 * Build a translator for one locale.
 *
 * Falls back to the default locale for a key the catalogue is missing — which
 * the type system prevents at build time, but a stale cached bundle or a
 * hand-written key cast to the key type can still produce at runtime. Showing
 * English is worse than showing the right language and better than showing
 * `checkout.payButton` to a customer.
 */
export const createTranslator = (catalogues, locale) => {
    const primary = catalogues[locale];
    const fallback = catalogues[DEFAULT_LOCALE];
    const t = (key, params) => {
        const message = lookup(primary, key) ?? lookup(fallback, key);
        if (message === undefined) {
            return key;
        }
        if (typeof message === 'string') {
            return interpolate(message, params, locale);
        }
        const count = params?.['count'];
        if (typeof count !== 'number') {
            // A plural message used without a count. `other` is the only form that
            // every language defines, so it is the safe choice.
            return interpolate(message.other, params, locale);
        }
        /**
         * `zero` is checked before asking Intl, deliberately.
         *
         * Most languages have no grammatical zero category, so Intl reports "other"
         * for 0 — but "0 items in your cart" reads badly in any of them, and
         * "Your cart is empty" is what a person would write. Honouring an explicit
         * `zero` lets a translator make that choice; omitting it falls through to
         * the grammatical rules.
         */
        if (count === 0 && message.zero !== undefined) {
            return interpolate(message.zero, params, locale);
        }
        const category = rulesFor(locale).select(count);
        const form = message[category] ?? message.other;
        return interpolate(form, params, locale);
    };
    return { locale, t };
};
//# sourceMappingURL=translate.js.map